import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { PutRoutingGuideType } from "~/shared/schemas/routing-guide-schema.ts";
import { postRoutingGuideSchema } from "~/shared/schemas/routing-guide-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { putAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

const putRoutingGuide = async (routingGuide: PutRoutingGuideType) => {
	const url = apiRoutes.PUTGLOBALROUTINGGUIDE.replace(
		":id",
		routingGuide.id.toString(),
	);
	const response = await putAndValidateTraxAr(
		url,
		routingGuide,
		postRoutingGuideSchema,
	);
	if (!response.success) {
		throw new Error(
			`Failed to create routing guide: ${response.error}`,
			response.details instanceof Error
				? { cause: response.details }
				: undefined,
		);
	}
	return response.data;
};

export const useUpdateRoutingGuide = () => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();

	const { mutate: updateRoutingGuide } = useMutation({
		mutationFn: (routingGuide: PutRoutingGuideType) =>
			putRoutingGuide(routingGuide),

		onSuccess: () => {
			showSuccess("Routing Guide Updated");
			void queryClient.invalidateQueries({ queryKey: ["globalRoutingGuides"] });
			void queryClient.invalidateQueries({
				queryKey: ["customerRoutingGuides"],
			});
		},

		onError: (_err) => {
			showError(`${_err}`);
		},
	});

	return { updateRoutingGuide };
};
